import type { ProfileId } from "@pax/api-contract/common";
import type { QueryClient } from "@tanstack/react-query";
import { api, orpc, reachable } from "@/lib/api";

/** How many friends the account panel lists before pointing at the profile page. */
export const PANEL_FRIEND_COUNT = 5;

/** How many published presets a profile page shows. */
const PROFILE_PRESET_COUNT = 24;

/** How many mutual friends a profile page shows. */
const MUTUAL_FRIEND_COUNT = 12;

export const profileQuery = (profileId: string) =>
  orpc.profiles.get.queryOptions({
    input: { params: { profileId } },
    select: response => response.profile,
  });

export const profileStatsQuery = (profileId: string) =>
  orpc.profiles.stats.get.queryOptions({
    input: { params: { profileId } },
    staleTime: 5 * 60_000,
    retry: false,
  });

export const profilePresetsQuery = (profileId: string) =>
  orpc.presets.list.queryOptions({
    input: { query: { ownerId: profileId as ProfileId, limit: PROFILE_PRESET_COUNT } },
    select: response => response.items,
    staleTime: 60_000,
  });

export const friendsQuery = (limit: number) =>
  orpc.friends.list.queryOptions({
    input: { query: { tab: "friends", limit } },
  });

export const relationshipQuery = (profileId: string) =>
  orpc.friends.relationships.get.queryOptions({
    input: { params: { profileId: profileId as ProfileId } },
  });

export const mutualFriendsQuery = (profileId: string) =>
  orpc.friends.mutualFriends.list.queryOptions({
    input: { params: { profileId: profileId as ProfileId }, query: { limit: MUTUAL_FRIEND_COUNT } },
  });

/** The four ways a friend button changes where you stand with someone. */
export type FriendChange = "request" | "cancel" | "accept" | "remove";

export async function changeFriendship(
  queryClient: QueryClient,
  profileId: string,
  change: FriendChange,
): Promise<void> {
  const id = profileId as ProfileId;
  if (change === "request")
    await reachable(api.friends.requests.create({ body: { profileId: id } }));
  if (change === "cancel")
    await reachable(api.friends.requests.delete({ params: { profileId: id } }));
  if (change === "accept")
    await reachable(api.friends.requests.accept({ params: { profileId: id } }));
  if (change === "remove") await reachable(api.friends.delete({ params: { profileId: id } }));
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: orpc.friends.key() }),
    queryClient.invalidateQueries({ queryKey: orpc.profiles.get.key() }),
  ]);
}
